import { useEffect, useMemo, useState } from "react";
import { useTelemetryContext } from "../contexts/TelemetryContext";
import { useLapTimerContext } from "../contexts/LapTimerContext";
import { useComparisonReport } from "../hooks/useReport";
import type { Session } from "../types/session";

const LIVE_BENCHMARK_STORAGE_KEY = "vtms.live-benchmark.selection.v1";

interface LiveBenchmarkPanelProps {
  sessions: Session[];
  activeSessionId: number | null;
}

function formatLapTime(ms: number | null) {
  if (ms === null) return "--:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
}

function formatDelta(ms: number | null) {
  if (ms === null) return "—";
  return `${ms > 0 ? "+" : ""}${(ms / 1000).toFixed(3)}s`;
}

function metricValue(metrics: Map<string, { value: number }>, key: string): number | null {
  const value = metrics.get(key)?.value;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getFastestLapKey(laps: Array<{ key: number; duration_ms: number }>): number | null {
  if (laps.length === 0) {
    return null;
  }

  return laps.reduce((best, lap) => (lap.duration_ms < best.duration_ms ? lap : best)).key;
}

type InsightTone = "fast" | "slow" | "neutral";

interface GuidanceInsight {
  title: string;
  body: string;
  tone: InsightTone;
}

function loadStoredSelection(activeSessionId: number | null) {
  if (activeSessionId === null || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(`${LIVE_BENCHMARK_STORAGE_KEY}.${activeSessionId}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as {
      benchmarkSessionId?: number;
      benchmarkLapId?: number;
      liveLapId?: number;
    };
    return {
      benchmarkSessionId: typeof parsed.benchmarkSessionId === "number" ? parsed.benchmarkSessionId : null,
      benchmarkLapId: typeof parsed.benchmarkLapId === "number" ? parsed.benchmarkLapId : null,
      liveLapId: typeof parsed.liveLapId === "number" ? parsed.liveLapId : null,
    };
  } catch {
    return null;
  }
}

function saveStoredSelection(
  activeSessionId: number | null,
  benchmarkSessionId: number | null,
  benchmarkLapId: number | null,
  liveLapId: number | null,
) {
  if (activeSessionId === null || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      `${LIVE_BENCHMARK_STORAGE_KEY}.${activeSessionId}`,
      JSON.stringify({ benchmarkSessionId, benchmarkLapId, liveLapId }),
    );
  } catch {
    // Ignore storage failures.
  }
}

export function LiveBenchmarkPanel({ sessions, activeSessionId }: LiveBenchmarkPanelProps) {
  const { metrics, gps } = useTelemetryContext();
  const lapTimer = useLapTimerContext();
  const storedSelection = useMemo(() => loadStoredSelection(activeSessionId), [activeSessionId]);
  const benchmarkCandidates = useMemo(
    () => sessions.filter((session) => session.ended_at !== null && session.lap_count > 0 && session.id !== activeSessionId),
    [activeSessionId, sessions],
  );
  const [selectedBenchmarkSessionId, setSelectedBenchmarkSessionId] = useState<number | null>(storedSelection?.benchmarkSessionId ?? benchmarkCandidates[0]?.id ?? null);
  const [selectedBenchmarkLapId, setSelectedBenchmarkLapId] = useState<number | null>(storedSelection?.benchmarkLapId ?? null);
  const [selectedLiveReferenceLapId, setSelectedLiveReferenceLapId] = useState<number | null>(storedSelection?.liveLapId ?? null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (benchmarkCandidates.length === 0) {
      setSelectedBenchmarkSessionId(null);
      return;
    }

    setSelectedBenchmarkSessionId((current) => {
      if (current !== null && benchmarkCandidates.some((session) => session.id === current)) {
        return current;
      }
      if (storedSelection?.benchmarkSessionId !== null && storedSelection?.benchmarkSessionId !== undefined) {
        const stored = benchmarkCandidates.find((session) => session.id === storedSelection.benchmarkSessionId);
        if (stored) {
          return stored.id;
        }
      }
      return benchmarkCandidates[0]?.id ?? null;
    });
  }, [benchmarkCandidates, storedSelection]);

  const { comparison, loading, error } = useComparisonReport(activeSessionId, selectedBenchmarkSessionId);

  const benchmarkSession = benchmarkCandidates.find((session) => session.id === selectedBenchmarkSessionId) ?? null;
  const liveReferenceLap = comparison?.reportA.laps.find((lap) => lap.key === selectedLiveReferenceLapId) ?? null;
  const benchmarkLap = comparison?.reportB.laps.find((lap) => lap.key === selectedBenchmarkLapId) ?? null;
  const benchmarkBest = comparison?.statsB.bestMs ?? null;
  const liveBest = lapTimer.bestLapTime;
  const liveVsBenchmarkBest = liveBest !== null && benchmarkBest !== null ? liveBest - benchmarkBest : null;
  const currentLapVsBenchmark = lapTimer.currentLapTime > 0 && benchmarkBest !== null
    ? lapTimer.currentLapTime - benchmarkBest
    : null;
  const rpm = metricValue(metrics, "RPM");
  const speed = metricValue(metrics, "SPEED");
  const throttle = metricValue(metrics, "THROTTLE_POS");

  useEffect(() => {
    if (!comparison) {
      setSelectedBenchmarkLapId(null);
      setSelectedLiveReferenceLapId(null);
      return;
    }

    const availableBenchmarkLaps = new Set(comparison.reportB.laps.map((lap) => lap.key));
    const availableLiveLaps = new Set(comparison.reportA.laps.map((lap) => lap.key));

    setSelectedBenchmarkLapId((current) => {
      if (current !== null && availableBenchmarkLaps.has(current)) {
        return current;
      }
      if (storedSelection?.benchmarkLapId !== null && storedSelection?.benchmarkLapId !== undefined && availableBenchmarkLaps.has(storedSelection.benchmarkLapId)) {
        return storedSelection.benchmarkLapId;
      }
      return getFastestLapKey(comparison.reportB.laps);
    });

    setSelectedLiveReferenceLapId((current) => {
      if (current !== null && availableLiveLaps.has(current)) {
        return current;
      }
      if (storedSelection?.liveLapId !== null && storedSelection?.liveLapId !== undefined && availableLiveLaps.has(storedSelection.liveLapId)) {
        return storedSelection.liveLapId;
      }
      return getFastestLapKey(comparison.reportA.laps);
    });
  }, [comparison, storedSelection]);

  useEffect(() => {
    saveStoredSelection(activeSessionId, selectedBenchmarkSessionId, selectedBenchmarkLapId, selectedLiveReferenceLapId);
  }, [activeSessionId, selectedBenchmarkSessionId, selectedBenchmarkLapId, selectedLiveReferenceLapId]);

  const sectorRows = useMemo(() => {
    if (!comparison || !liveReferenceLap || !benchmarkLap) {
      return [] as Array<{ sector: number; liveMs: number | null; benchmarkMs: number | null; deltaMs: number | null }>;
    }

    const liveSectors = comparison.reportA.sectors[String(liveReferenceLap.key)] ?? [];
    const benchmarkSectors = comparison.reportB.sectors[String(benchmarkLap.key)] ?? [];
    const sectorNumbers = [...new Set([...liveSectors.map((sector) => sector.sector_num), ...benchmarkSectors.map((sector) => sector.sector_num)])]
      .sort((left, right) => left - right);

    return sectorNumbers.map((sector) => {
      const liveMs = liveSectors.find((entry) => entry.sector_num === sector)?.duration_ms ?? null;
      const benchmarkMs = benchmarkSectors.find((entry) => entry.sector_num === sector)?.duration_ms ?? null;
      return {
        sector,
        liveMs,
        benchmarkMs,
        deltaMs: liveMs !== null && benchmarkMs !== null ? liveMs - benchmarkMs : null,
      };
    });
  }, [benchmarkLap, comparison, liveReferenceLap]);

  const biggestSectorGap = sectorRows
    .filter((row) => row.deltaMs !== null)
    .sort((left, right) => (right.deltaMs ?? -Infinity) - (left.deltaMs ?? -Infinity))[0] ?? null;

  const liveReferenceDelta = liveReferenceLap && benchmarkLap
    ? liveReferenceLap.duration_ms - benchmarkLap.duration_ms
    : null;

  const benchmarkHeadline = benchmarkLap
    ? `Target lap ${benchmarkLap.lap_number} · ${formatLapTime(benchmarkLap.duration_ms)}`
    : "Choose a benchmark lap";

  const guidanceInsights = useMemo(() => {
    const insights: GuidanceInsight[] = [];

    if (lapTimer.predictedLapDelta !== null) {
      if (lapTimer.predictedLapDelta <= 0) {
        insights.push({
          title: liveVsBenchmarkBest !== null && liveVsBenchmarkBest > 0 ? "Benchmark is within reach" : "Current trend is strong",
          body:
            liveVsBenchmarkBest !== null && liveVsBenchmarkBest > 0
              ? `Projected ${formatDelta(lapTimer.predictedLapDelta)} versus session best. Matching that pace would still leave ${formatDelta(liveVsBenchmarkBest)} to recover versus the benchmark best.`
              : `Projected ${formatDelta(lapTimer.predictedLapDelta)} versus session best. Keep this pace to protect the gain already on the board.`,
          tone: "fast",
        });
      } else {
        insights.push({
          title: "Current lap is drifting off pace",
          body: `Projected ${formatDelta(lapTimer.predictedLapDelta)} versus session best. The benchmark stays out of reach until this lap starts gaining time back.`,
          tone: "slow",
        });
      }
    } else if (currentLapVsBenchmark !== null) {
      insights.push({
        title: currentLapVsBenchmark <= 0 ? "Current lap is ahead" : "Current lap is behind",
        body: `The in-progress lap is ${formatDelta(currentLapVsBenchmark)} relative to the benchmark best based on elapsed lap time alone.`,
        tone: currentLapVsBenchmark <= 0 ? "fast" : "slow",
      });
    }

    if (biggestSectorGap) {
      insights.push({
        title: `Sector ${biggestSectorGap.sector} is the biggest give-up`,
        body: `The selected live reference lap lost ${formatDelta(biggestSectorGap.deltaMs)} there versus benchmark lap ${benchmarkLap?.lap_number ?? "—"}. That is still the clearest place to go hunting for time.`,
        tone: (biggestSectorGap.deltaMs ?? 0) <= 0 ? "fast" : "slow",
      });
    }

    if (liveReferenceLap && benchmarkLap && liveReferenceDelta !== null) {
      insights.push({
        title: "Reference gap stays measurable",
        body:
          lapTimer.lastDelta !== null
            ? `Live reference lap ${liveReferenceLap.lap_number} sits ${formatDelta(liveReferenceDelta)} off benchmark lap ${benchmarkLap.lap_number}. The last completed live lap finished ${formatDelta(lapTimer.lastDelta)} versus session best.`
            : `Live reference lap ${liveReferenceLap.lap_number} sits ${formatDelta(liveReferenceDelta)} off benchmark lap ${benchmarkLap.lap_number}.`,
        tone: liveReferenceDelta <= 0 ? "fast" : "neutral",
      });
    }

    return insights.slice(0, 3);
  }, [benchmarkLap, biggestSectorGap, currentLapVsBenchmark, lapTimer.lastDelta, lapTimer.predictedLapDelta, liveReferenceDelta, liveReferenceLap, liveVsBenchmarkBest]);

  if (activeSessionId === null) {
    return (
      <div className="live-benchmark live-benchmark--idle">
        <span>No active session &mdash; benchmark available during sessions</span>
      </div>
    );
  }

  return (
    <div className={`live-benchmark ${drawerOpen ? "live-benchmark--open" : ""}`}>
      <div className="live-benchmark__summary">
        <div>
          <span className="live-benchmark__eyebrow">Live benchmark</span>
          <strong>{benchmarkSession?.label ?? "Select benchmark"}</strong>
        </div>
        <div className="live-benchmark__chips">
          <span className="live-benchmark__chip">Lap {lapTimer.currentLapNumber || "—"}</span>
          <span className={`live-benchmark__chip ${currentLapVsBenchmark !== null && currentLapVsBenchmark <= 0 ? "is-fast" : "is-slow"}`}>
            Current {formatDelta(currentLapVsBenchmark)}
          </span>
          <span className="live-benchmark__chip">{benchmarkHeadline}</span>
          <button className="live-benchmark__toggle" onClick={() => setDrawerOpen((open) => !open)}>
            {drawerOpen ? "Hide benchmark" : "Open benchmark"}
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="live-benchmark__drawer">
          <div className="live-benchmark__controls">
            <label>
              <span>Benchmark session</span>
              <select
                aria-label="Benchmark session"
                value={selectedBenchmarkSessionId ?? ""}
                onChange={(event) => setSelectedBenchmarkSessionId(event.target.value ? Number(event.target.value) : null)}
              >
                {benchmarkCandidates.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.label} · Best {formatLapTime(session.best_ms)}
                  </option>
                ))}
              </select>
            </label>
            {comparison && comparison.reportB.laps.length > 0 && (
              <label>
                <span>Benchmark lap</span>
                <select
                  aria-label="Benchmark lap"
                  value={selectedBenchmarkLapId ?? ""}
                  onChange={(event) => setSelectedBenchmarkLapId(event.target.value ? Number(event.target.value) : null)}
                >
                  {comparison.reportB.laps.map((lap) => (
                    <option key={lap.key} value={lap.key}>
                      Lap {lap.lap_number} · {formatLapTime(lap.duration_ms)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {comparison && comparison.reportA.laps.length > 0 && (
              <label>
                <span>Live reference lap</span>
                <select
                  aria-label="Live reference lap"
                  value={selectedLiveReferenceLapId ?? ""}
                  onChange={(event) => setSelectedLiveReferenceLapId(event.target.value ? Number(event.target.value) : null)}
                >
                  {comparison.reportA.laps.map((lap) => (
                    <option key={lap.key} value={lap.key}>
                      Lap {lap.lap_number} · {formatLapTime(lap.duration_ms)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="live-benchmark__grid">
            <section className="live-benchmark__card">
              <h3>Current pace</h3>
              <div className="live-benchmark__stats">
                <div>
                  <span>Current lap</span>
                  <strong>{lapTimer.currentLapNumber > 0 ? formatLapTime(lapTimer.currentLapTime) : "Waiting"}</strong>
                </div>
                <div>
                  <span>Best this session</span>
                  <strong>{formatLapTime(liveBest)}</strong>
                </div>
                <div>
                  <span>Predicted delta</span>
                  <strong className={lapTimer.predictedLapDelta !== null && lapTimer.predictedLapDelta <= 0 ? "analysis-delta-fast" : "analysis-delta-slow"}>
                    {formatDelta(lapTimer.predictedLapDelta)}
                  </strong>
                </div>
                <div>
                  <span>Last completed delta</span>
                  <strong className={lapTimer.lastDelta !== null && lapTimer.lastDelta <= 0 ? "analysis-delta-fast" : "analysis-delta-slow"}>
                    {formatDelta(lapTimer.lastDelta)}
                  </strong>
                </div>
              </div>
            </section>

            <section className="live-benchmark__card">
              <h3>Benchmark pace</h3>
              {loading ? (
                <p className="live-benchmark__empty">Loading benchmark comparison…</p>
              ) : error ? (
                <p className="live-benchmark__empty">{error}</p>
              ) : benchmarkSession ? (
                <div className="live-benchmark__stats">
                  <div>
                    <span>Benchmark best</span>
                    <strong>{formatLapTime(benchmarkBest)}</strong>
                  </div>
                  <div>
                    <span>Selected benchmark lap</span>
                    <strong>{benchmarkLap ? formatLapTime(benchmarkLap.duration_ms) : "—"}</strong>
                  </div>
                  <div>
                    <span>Live best delta</span>
                    <strong className={liveVsBenchmarkBest !== null && liveVsBenchmarkBest <= 0 ? "analysis-delta-fast" : "analysis-delta-slow"}>
                      {formatDelta(liveVsBenchmarkBest)}
                    </strong>
                  </div>
                  <div>
                    <span>Reference lap delta</span>
                    <strong className={liveReferenceDelta !== null && liveReferenceDelta <= 0 ? "analysis-delta-fast" : "analysis-delta-slow"}>
                      {formatDelta(liveReferenceDelta)}
                    </strong>
                  </div>
                </div>
              ) : (
                <p className="live-benchmark__empty">End a prior session to use it as a benchmark.</p>
              )}
            </section>

            <section className="live-benchmark__card">
              <h3>Live telemetry</h3>
              <div className="live-benchmark__stats">
                <div>
                  <span>RPM</span>
                  <strong>{rpm !== null ? Math.round(rpm).toString() : "—"}</strong>
                </div>
                <div>
                  <span>Speed</span>
                  <strong>{speed !== null ? Math.round(speed).toString() : "—"}</strong>
                </div>
                <div>
                  <span>Throttle</span>
                  <strong>{throttle !== null ? `${throttle.toFixed(0)}%` : "—"}</strong>
                </div>
              </div>
            </section>

            <section className="live-benchmark__card">
              <h3>Track guidance</h3>
              {biggestSectorGap ? (
                <div className="live-benchmark__guidance">
                  <p>
                    Largest gap is in <strong>Sector {biggestSectorGap.sector}</strong> at <strong>{formatDelta(biggestSectorGap.deltaMs)}</strong> versus the selected benchmark lap.
                  </p>
                  <p>
                    Current lap is {currentLapVsBenchmark !== null ? formatDelta(currentLapVsBenchmark) : "—"} against the benchmark best while still in progress.
                  </p>
                </div>
              ) : (
                <p className="live-benchmark__empty">
                  Full live telemetry overlay is not available yet. This board tracks current pace live and compares completed laps against the selected benchmark.
                </p>
              )}
            </section>

            <section className="live-benchmark__card live-benchmark__card--wide">
              <h3>Coaching cues</h3>
              {guidanceInsights.length > 0 ? (
                <div className="live-benchmark__insights">
                  {guidanceInsights.map((insight) => (
                    <article
                      key={insight.title}
                      className={`live-benchmark__insight live-benchmark__insight--${insight.tone}`}
                    >
                      <strong>{insight.title}</strong>
                      <p>{insight.body}</p>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="live-benchmark__empty">Benchmark guidance will appear after VTMS has enough lap context to compare.</p>
              )}
            </section>

            <section className="live-benchmark__card live-benchmark__card--wide">
              <h3>Sector targets</h3>
              {sectorRows.length > 0 ? (
                <table className="live-benchmark__sector-table">
                  <thead>
                    <tr>
                      <th>Sector</th>
                      <th>Live ref</th>
                      <th>Benchmark</th>
                      <th>Gap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectorRows.map((row) => (
                      <tr key={row.sector}>
                        <td>S{row.sector}</td>
                        <td>{formatLapTime(row.liveMs)}</td>
                        <td>{formatLapTime(row.benchmarkMs)}</td>
                        <td className={row.deltaMs !== null && row.deltaMs <= 0 ? "analysis-delta-fast" : "analysis-delta-slow"}>
                          {formatDelta(row.deltaMs)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="live-benchmark__empty">No sector targets available for the selected laps.</p>
              )}
            </section>

            <section className="live-benchmark__card">
              <h3>Track context</h3>
              <div className="live-benchmark__stats">
                <div>
                  <span>Sector</span>
                  <strong>{lapTimer.currentSectorNumber || "—"}</strong>
                </div>
                <div>
                  <span>Sector time</span>
                  <strong>{lapTimer.currentSectorTime > 0 ? formatLapTime(lapTimer.currentSectorTime) : "—"}</strong>
                </div>
                <div>
                  <span>GPS</span>
                  <strong>{gps.latitude !== null && gps.longitude !== null ? "Live" : "No fix"}</strong>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
